import SinglePageHeader from "../../../components/Page/SinglePageHeader";
import { useEffect, useState, MouseEvent, ReactNode, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ReactComponent as RecordsIcon } from '../../../assets/Icons/adornments/Records.svg';
import { ReactComponent as LowIcon } from '../../../assets/Icons/arrows/arrow-down-right.svg';
import { ReactComponent as HighIcon } from '../../../assets/Icons/arrows/arrow-up-right.svg';


import SnackBar from "../../../components/Alerts/SnackBar";
import useMessage from "../../../hooks/useMessage";
import NestedDropdown from "../../../components/Menus&Dropdowns/NestedDropdown";
import { TableData, TableHeader } from "../../../components/Table/DataTable";
import { useGetDatasetDataframeDatasetDataframeDatasetHashGet, useGetDatasetProtocolSizeDatasetProtocolSizeDatasetHashGet, useGetDatasetsDatasetGet } from "../../../api/generated/fastAPI";
import { Dataset, DatasetMetadataOutputColumnMetadata } from "../../../api/models";
import ColumnsButton from "../../../components/Buttons/ColumnsButton";
import ListGridToggle from "../../../components/Buttons/ListGridToggle";
import Pagination from "../../../components/Filtering/Pagination";
import ColumnMetadataMenu from "../../../components/Menus&Dropdowns/ColumnMetadataMenu";
import usePaginate from "../../../hooks/usePaginate";
import { DropdownOption } from "../../../components/Menus&Dropdowns/Dropdown";
import TrainModelModal from "../../Models/components/TrainModelModal";
import CurateDatasetModal from "../components/CurateDatasetModal";
import BatchPredictionModal from "../../Resultsets/components/BatchPredictionModal";
import DeleteModal from "../../../components/Popups/DeleteModal";
import NewPageTabs from "../../../components/Tabs/NewPageTabs";
import GenerateReadAcrossModal from "../../ReadAcross/components/GenerateReadAcrossModal";
import SingleCompoundPredictionModal from "../../Resultsets/components/SingleCompoundPredictionModal";
import { exportFileExtensions } from "../../../utils/supplementalData";
import useDelete from "../../../hooks/endpoints/useDelete";
import useUpdate from "../../../hooks/endpoints/useUpdate";
import { useAccess } from "../../../access/useAccess";
import { ac_access, demo_access, readacross_access } from "../../../access/predefinedConditions";
import useDownload from "../../../hooks/endpoints/useDownload";
import VirtualizedDataTable from "../../../components/Table/VirtualizedDataTable";
import { formatDatasetDataframe, formatDisplayedDatasetMetadata, MetadataMenuItemType } from "../../../utils/dataFormattingFns";
import UpdatedMetadataMenu from "../../../components/Menus&Dropdowns/UpdatedMetadataMenu";
import VirtualizedGridList from "../../../components/Grid/VirtualizedGridList";
import { convertNumberToDecimal } from "../../../utils/formattingFns";

const tabs = [ 'Data', 'Protocol' ];

export default function IndividualDataset() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { canAccess } = useAccess();
    const { downloadDataset } = useDownload();

    const {
        open,
        setOpen,
        message,
        isSuccess,
        setPopupMessage
    } = useMessage();

    const {
        setItems,
        setItemsPerPage,
        setCurrentPage,
        currentPage,
        paginatedItems,
        startIndex,
        endIndex,
        totalPages,
        paginate,
    } = usePaginate();

    const [ headers, setHeaders ] = useState<TableHeader[]>([]);
    const [ isGrid, setIsGrid ] = useState(false);

    const dataframeMutation = useGetDatasetDataframeDatasetDataframeDatasetHashGet(id ? id : '', {
        applicability_domain: true,
    });
    const datasetMutation = useGetDatasetsDatasetGet({ dataset_hash: id ? id : '' });
    
    const [ dataset, setDataset ] = useState<Dataset | null>(null);
    const [ dataframeData, setDataframeData ] = useState<{
        [key: string]: {
            [key: number]: string | number,
        }
    } | null>(null);
    const [ dataframe, setDataFrame ] = useState<TableData[]>([]);
    const [ datasetMetadata, setDatasetMetadata ] = useState<MetadataMenuItemType[]>([]);

    const protocolSizeMutation = useGetDatasetProtocolSizeDatasetProtocolSizeDatasetHashGet(id ? id : '');
    const [ hasProtocol, setHasProtocol ] = useState(false);

    const [ selectedIndex, setSelectedIndex ] = useState(0);
    const [ dropdownOptions, setDropdownOptions ] = useState<DropdownOption[]>([]);

    const [openTrainModelId, setOpenTrainModelId] = useState<string | null | undefined>(null);
    const [openBatchPredictionId, setOpenBatchPredictionId] = useState<string | null | undefined>(null);
        
    useEffect(() => {
        if (datasetMutation.data) {
            const data = datasetMutation.data as unknown as Dataset
            setDataset(data);
        }
    }, [datasetMutation.data]);
    
    useEffect(() => {
        const fetchDsMetadata = async (ds: Dataset) => {
            const metadata = await formatDisplayedDatasetMetadata(ds);
            setDatasetMetadata(metadata);
        }

        if (dataset) {
            fetchDsMetadata(dataset);
        }
    }, [dataset]);


    useEffect(() => {
        if (dataframeMutation.data) {
            const data = dataframeMutation.data as unknown as {
                [key: string]: {
                    [key: number]: string | number,
                }
            };
            setDataframeData(data);
        }
    }, [dataframeMutation.data]);

    useEffect(() => {
        if (protocolSizeMutation.data) {
            const data = protocolSizeMutation.data as unknown as number;
            const kbs = data / 1000;
            if (kbs <= 1) {
                setHasProtocol(false);
            } else {
                setHasProtocol(true);
            }
        }
    }, [protocolSizeMutation.data]);

    const { deleteDatasets } = useDelete({ setPopupMessage });
    const { updateDataset } = useUpdate({ 
        setPopupMessage, 
        refetchFn: () => datasetMutation.refetch(), 
        itemToUpdate: dataset 
    })


    useEffect(() => {
        if (dataset !== null && dataframeData !== null && Object.keys(dataframeData).length > 0) {
            let { dsData, dsHeaders } = formatDatasetDataframe(dataframeData);
            const columnMetadata = dataset.dataset_metadata!.column_metadata;

            
            dsHeaders = dsHeaders.map(header => ({
                ...header,
                columnMetadata: getColumnMetadata(header.value, columnMetadata),
            }));

            dsData = dsData.map(data => {
                const options: DropdownOption[] = [];

                const smilesField = data.fields.find(field => field.type === 'structure');
                const smiles = smilesField ? smilesField.value.toString() : undefined;

                if (canAccess({or: readacross_access})) options.push(
                    {
                        label: 'Generate Read-Across',
                        btnComp: (
                            <GenerateReadAcrossModal 
                                setPopupMessage={setPopupMessage}
                                selectedCompound={smiles}
                                location="dropdown"
                            />
                        )
                    },
                );

                options.push(
                    {
                    label: 'Run Prediction',
                        btnComp: (
                            <SingleCompoundPredictionModal 
                                setPopupMessage={setPopupMessage}
                                selectedCompound={smiles}
                                location="dropdown"
                            />
                        )
                    }
                );

                return {
                    ...data,
                    options,
                }

            });

            setHeaders(dsHeaders);
            setDataFrame(dsData);
            setItems(dsData);
        }
        //eslint-disable-next-line
    }, [dataframeData, dataset]);

    useEffect(() => {
        let options: DropdownOption[] = [
            {
                label: 'Curate Dataset',
                btnComp: (
                    <CurateDatasetModal 
                        selectedDatasetId={id ? id : ''}
                        setPopupMessage={setPopupMessage} 
                    />
                )
            }
        ];

        if (canAccess({or: ac_access})) options.push(
            {
                label: 'Train Model',
                action: () => setOpenTrainModelId(id), // Only trigger open
            }
        );

        options.push(
            {
                label: 'Run Prediction',
                action: () => setOpenBatchPredictionId(id), // Only trigger open
            },
            {
                label: 'Export',
                moreOptions: exportFileExtensions.map(ext => (
                    {
                        label: ext.label,
                        action: async (e: MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                            if (dataset && dataframeData) {
                                try {
                                    await downloadDataset(
                                        ext.file_type,
                                        dataset,
                                        dataframeData,
                                    );

                                    setPopupMessage('You have successfully downloaded the selected dataset.', true);
                                } catch (err:any) {
                                    console.log(err);
                                    setPopupMessage('There was an error downloading this dataset. Please try again.', false);
                                }
                            } 
                        }
                    }
                ))
            },
        );
        if (canAccess({or: demo_access})) options.push(
            {
                label: 'Delete',
                btnComp: (
                    <DeleteModal 
                        items={[{
                            type: 'dataset',
                            id: id ? id : '',
                            name: dataset && dataset.name ? dataset.name : '',
                        }]}
                        deleteFn={deleteDatasets}
                    />
                )
            },
        );

        setDropdownOptions(options);

        //eslint-disable-next-line
    }, [id, dataframe, dataset, dataframeData]);

    const getColumnMetadata = (key: string, columnMetadata: DatasetMetadataOutputColumnMetadata | undefined) => {
        if (columnMetadata) {
            const metadata = columnMetadata[key];
            if (metadata) {
                const { data_type, stats, description, units, class_origin } = metadata;
                const associatedMetadata = {
                    column_name: key,
                    data_type,
                    stats,
                    description,
                    units,
                    class_origin,
                }

                return <ColumnMetadataMenu label="Column Metadata" columnMetadata={associatedMetadata}/>
            } else {
                return undefined;
            }
        } else {
            return undefined;
        }
    };

    const itemMetadata = useMemo(() => {
        if (!dataset) return [];

        const dsMetadata = dataset.dataset_metadata!;

        const { records_number, column_metadata, dataset_types } = dsMetadata;
        const stats = (dataset as any).dataset_statistics;

        const metadata: {
            label: string,
            value: string | string[],
            adornment?: ReactNode,
        }[] = [{
            label: 'Records',
            value: records_number ? records_number.toString() : '0',
            adornment: <RecordsIcon />,
        }];

        let statsObject: {
            actives: number | null,
            high_value: number | null,
            inactives: number | null,
            low_value: number | null,
            record_number: number | null,
        } | undefined = undefined;

        if (column_metadata && dataset_types && stats) {
            const dsStats = Object.entries(stats);
            const columnMetadata = Object.entries(column_metadata);

            if (dataset_types.length === 0 || dataset_types[0] === 'structures-only') {
                const structureMetadata = columnMetadata.find(column => column[1] && column[1].data_type && column[1].data_type === 'structure');
                if (structureMetadata) {
                    const structureStats = dsStats.find(s => s[0] === structureMetadata[0]);
                    if (structureStats) statsObject = structureStats[1] as any;
                }
            } else if (dataset_types[0] === 'single-class-label') {
                const singleClassMetadata = columnMetadata.find(column => column[1] && column[1].data_type && column[1].data_type === 'single-class-label');
                if (singleClassMetadata) {
                    const singleClassStats = dsStats.find(s => s[0] === singleClassMetadata[0]);
                    if (singleClassStats) statsObject = singleClassStats[1] as any;
                }
            } else if (dataset_types[0] === 'continuous-value') {
                const continuousMetadata = columnMetadata.find(column => column[1] && column[1].data_type && column[1].data_type === 'continuous-value');
                if (continuousMetadata) {
                    const continuousStats = dsStats.find(s => s[0] === continuousMetadata[0]);
                    if (continuousStats) statsObject = continuousStats[1] as any;
                }
            }
        };

        if (statsObject) {
            const { actives, inactives, high_value, low_value } = statsObject;

            if (actives !== undefined && actives !== null && inactives !== undefined && inactives !== null) {
                metadata.push({
                    label: 'Actives',
                    value: actives.toString(),
                    adornment: <div className="bg-[#C7DB78] w-[16px] rounded-full aspect-square" />,
                });

                metadata.push({
                    label: 'Inactives',
                    value: inactives.toString(),
                    adornment: <div className="bg-[#FF8484] w-[16px] rounded-full aspect-square" />
                });
            } else if (high_value !== undefined && high_value !== null && low_value !== undefined && low_value !== null) {
                metadata.push({
                    label: 'High Value',
                    value: convertNumberToDecimal(high_value),
                    adornment: <HighIcon />
                });

                metadata.push({
                    label: 'Low Value',
                    value: convertNumberToDecimal(low_value),
                    adornment: <LowIcon />
                });
            }
        }

        return metadata;
    }, [dataset]);
    

    const handleTabChange = (event: React.SyntheticEvent, index: number) => {
        setSelectedIndex(index);
        if (index === 1) {
            navigate('protocol')
        }
    }


    return (
        <div className={`p-10 h-screen overflow-hidden flex flex-col justify-start items-stretch gap-4`}>
                <TrainModelModal
                    selectedDatasetId={id}
                    setPopupMessage={setPopupMessage} 
                    isDropdownItem
                    open={openTrainModelId === id}
                    setOpen={(val) => setOpenTrainModelId(val ? id : null)}
                />
                <BatchPredictionModal
                    selectedDatasetId={id}
                    setPopupMessage={setPopupMessage}
                    isPrimaryMenu={false}
                    isHidden
                    open={openBatchPredictionId === id}
                    setOpen={(val) => setOpenBatchPredictionId(val ? id : null)}
                />

                <SinglePageHeader 
                    label={dataset ? dataset.name! : ''} 
                    isLoading={datasetMutation.isLoading} 
                    pages={[
                        {
                            label: 'Datasets',
                            action: (e: MouseEvent<HTMLButtonElement>) => { navigate('/datasets') }
                        },
                        {
                            label: dataset ? dataset.name! : '',
                        }
                    ]}
                    itemMetadata={itemMetadata}
                >
                    <NestedDropdown
                        dir="right"
                        btn={{
                            label: 'Options',
                            type: 'primary',
                            styling: 'solid',
                            id: 'single-ds-options'
                        }}
                        options={dropdownOptions}
                    />
                    {datasetMetadata.length > 0 && (
                        <UpdatedMetadataMenu 
                            menuLabel="Dataset Metadata"
                            menuItems={datasetMetadata}
                            updateFunc={updateDataset}
                            location="page"
                        />
                    )}
                </SinglePageHeader>

                {hasProtocol && (
                    <div>
                        <NewPageTabs 
                            tabs={tabs.map((tab,i) => ({
                                label: tab,
                                index: i
                            }))}
                            selectedIndex={selectedIndex}
                            handleTabChange={handleTabChange}
                        />
                    </div>
                )}

                <div className="w-full flex justify-between items-center gap-4">
                    <div className="max-w-[600px] w-full">
                        {/* <SearchBar/> */}
                    </div>
                    <div className="flex justify-end items-center gap-4">
                        <ColumnsButton 
                            headers={headers} 
                            setHeaders={setHeaders} 
                            isDisabled={false}
                        />
                        <ListGridToggle setIsGrid={setIsGrid} isGrid={isGrid}/>
                    </div>
                </div>
               

                <>
                {isGrid ? (
                    <>
                        <VirtualizedGridList 
                            isLoading={dataframeMutation.isLoading || datasetMutation.isLoading} 
                            gridData={paginatedItems}
                            headers={headers}
                            setPopupMessage={setPopupMessage}
                        />
                    </>
                ) : (
                    <>
                        <VirtualizedDataTable 
                            isLoading={dataframeMutation.isLoading || datasetMutation.isLoading} 
                            headers={headers} 
                            tableData={paginatedItems}
                            setPopupMessage={setPopupMessage}
                            fixedItemHeight={194}
                        />                   
                    </>
                    )}

                </>

                <Pagination
                    setItemsPerPage={setItemsPerPage}
                    setCurrentPage={setCurrentPage}
                    currentPage={currentPage}
                    startIndex={startIndex}
                    endIndex={endIndex}
                    totalPages={totalPages}
                    total={dataframe.length}
                    paginate={paginate}
                />

                <SnackBar open={open} setOpen={setOpen} message={message} isSuccess={isSuccess}/>
           

        </div>
    )
}